import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRoles";
import { ROLE_PRECEDENCE } from "@/lib/auth/rolePrecedence";

/**
 * Holds the dashboard's active "Viewing as" role for multi-hat users.
 * Switching ONLY re-keys which recipe the engine requests — the server
 * validates the role against the caller's held roles (ADMIN may view any)
 * and gates every widget on the caller's own permission claims, so a
 * switch can never widen data access.
 *
 * The last choice persists per user via localStorage (the established
 * client-preference pattern — see MyToggle).
 */
interface DashboardRoleContextValue {
  /** The role whose recipe the dashboard renders (null until auth resolves). */
  activeRole: string | null;
  setActiveRole: (role: string) => void;
  /** Roles offered by the switcher, precedence-ordered. ADMIN gets every
   *  known role (support/QA "view as" mode); everyone else gets their own. */
  viewAsOptions: string[];
  /** Render the switcher only for multi-hat users (or ADMIN). */
  canSwitch: boolean;
  /** code → display name, from the canonical roles lookup. */
  roleTitle: (code: string) => string;
}

const DashboardRoleContext = createContext<DashboardRoleContextValue | undefined>(undefined);

function storageKey(userId: string | undefined) {
  return `dashboard:viewAs:${userId ?? "anon"}`;
}

function readStored(userId: string | undefined): string | null {
  try {
    return window.localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function byPrecedence(codes: string[]): string[] {
  return [...codes].sort((a, b) => {
    const ia = ROLE_PRECEDENCE.indexOf(a);
    const ib = ROLE_PRECEDENCE.indexOf(b);
    return (ia === -1 ? ROLE_PRECEDENCE.length : ia) - (ib === -1 ? ROLE_PRECEDENCE.length : ib);
  });
}

export function DashboardRoleProvider({ children }: { children: ReactNode }) {
  const { user, primaryRoleCode, hasRoleCode } = useAuth();
  const isAdmin = hasRoleCode("ADMIN");
  const heldRoles = useMemo(() => user?.roleCodes ?? [], [user?.roleCodes]);

  // Roles lookup is only needed when a switcher can actually render.
  const rolesQuery = useRoles();
  const knownRoles = rolesQuery.data ?? [];

  const viewAsOptions = useMemo(() => {
    if (isAdmin) {
      const all = knownRoles.map((r) => r.code);
      // The lookup may lag a freshly added role the admin holds — union in.
      for (const held of heldRoles) if (!all.includes(held)) all.push(held);
      return byPrecedence(all);
    }
    return byPrecedence([...heldRoles]);
  }, [isAdmin, knownRoles, heldRoles]);

  const isValidChoice = useCallback(
    (role: string | null): role is string =>
      !!role && (isAdmin ? true : heldRoles.includes(role)),
    [isAdmin, heldRoles],
  );

  const [stored, setStored] = useState<string | null>(() => readStored(user?.id));

  // Auth resolves after mount (and the user can change on re-login) — re-read
  // the persisted choice whenever the storage key changes.
  useEffect(() => {
    setStored(readStored(user?.id));
  }, [user?.id]);

  // A stale persisted choice (role removed from the user) silently falls
  // back to the effective primary role.
  const activeRole = isValidChoice(stored) ? stored : primaryRoleCode;

  const setActiveRole = useCallback(
    (role: string) => {
      if (!isValidChoice(role)) return;
      setStored(role);
      try {
        window.localStorage.setItem(storageKey(user?.id), role);
      } catch {
        /* localStorage unavailable — the choice just won't persist */
      }
    },
    [isValidChoice, user?.id],
  );

  const roleTitle = useCallback(
    (code: string) => knownRoles.find((r) => r.code === code)?.displayName ?? code,
    [knownRoles],
  );

  const value = useMemo<DashboardRoleContextValue>(
    () => ({
      activeRole,
      setActiveRole,
      viewAsOptions,
      canSwitch: viewAsOptions.length > 1,
      roleTitle,
    }),
    [activeRole, setActiveRole, viewAsOptions, roleTitle],
  );

  return <DashboardRoleContext.Provider value={value}>{children}</DashboardRoleContext.Provider>;
}

export function useDashboardRole(): DashboardRoleContextValue {
  const ctx = useContext(DashboardRoleContext);
  if (!ctx) throw new Error("useDashboardRole must be used within <DashboardRoleProvider>.");
  return ctx;
}

/** Null-safe variant for components (TopBar) that also render on routes /
 *  tests without the provider. */
export function useOptionalDashboardRole(): DashboardRoleContextValue | null {
  return useContext(DashboardRoleContext) ?? null;
}
